/**
 * Copy + derived state for SMS onboarding checklist, wizard close, and
 * Text Messaging hand-holding (Sessions 5b + 5c).
 */

import { AUTO_RECHARGE_BILLING_HREF } from './walletAutoRecharge';

export const CHECKLIST_TEXTING_LABEL = 'Add texting (optional add-on)';
export const CHECKLIST_TEXTING_SUB =
  'Give leads a number to text and get texted back when you miss a call.';

export const WIZARD_SMS_CLOSING_LINE = 'Want leads to text you? Add texting after you go live.';

export const PAST_DUE_STATUS_LABEL = 'Your number is paused — top up to resume texting';

export const SOLE_PROP_MOBILE_OTP_COPY =
  "After you submit, you'll receive a text with a verification code at this number — replying completes your carrier registration.";

export const MISSED_CALL_CHECKBOX_ONELINER =
  "Callers who don't reach you get one text within a minute; they can reply and the AI answers.";

export const AUTO_RECHARGE_NEXT_STEPS_SUB =
  "Keep a balance so texts don't silently stop.";

export const EXTERNALLY_MANAGED_FORWARD_SUB =
  'Managed outside gocta — contact support to change call routing.';

export const EXTERNALLY_MANAGED_TEXTBACK_SUB = 'Not available on this number';

/** Checklist / onboardingComplete: live number + approved campaign. past_due is not done. */
export function isSmsTextingChecklistDone(smsNumber) {
  return smsNumber?.status === 'active' && smsNumber?.a2p?.status === 'approved';
}

export function statusLabel(smsNumber) {
  if (!smsNumber) return null;
  const { status, a2p, otpStatus, brandType } = smsNumber;
  if (status === 'past_due') return PAST_DUE_STATUS_LABEL;
  if (status === 'active' && a2p?.status === 'approved') return 'Active';
  if (a2p?.status === 'failed') return 'Failed';
  if (a2p?.status === 'campaign_pending') return 'Campaign under review';
  if (a2p?.status === 'brand_pending' && brandType === 'SOLE_PROPRIETOR' && otpStatus === 'pending') {
    return 'OTP verification pending';
  }
  if (a2p?.status === 'brand_pending') return 'Brand under review';
  if (a2p?.status === 'not_started') return 'Registration pending';
  return 'Provisioning';
}

export function maskUsPhoneForToast(e164) {
  const digits = String(e164 || '').replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return 'your number';
  return `(${ten.slice(0, 3)}) ••• ••••`;
}

function stepRecord(id, label, state, extras = {}) {
  return { id, label, state, ...extras };
}

/**
 * Pending-state stepper. Null when there is no in-flight registration
 * (no doc, past_due, released, or already live+approved).
 */
export function buildSmsPendingStepper(smsNumber) {
  if (!smsNumber) return null;
  if (smsNumber.status === 'past_due' || smsNumber.status === 'released') return null;
  if (smsNumber.status === 'active' && smsNumber.a2p?.status === 'approved') return null;

  const a2p = smsNumber.a2p?.status || 'not_started';
  const failed = a2p === 'failed';
  const isSoleProp = smsNumber.brandType === 'SOLE_PROPRIETOR';
  const otpPending = smsNumber.otpStatus === 'pending';
  const otpConfirmed = smsNumber.otpStatus === 'confirmed';
  const hasPhone = Boolean(smsNumber.phoneNumber);

  const otpActive = isSoleProp && otpPending && a2p === 'brand_pending';
  const brandActive = a2p === 'brand_pending' && !otpActive && !failed;
  const brandDone =
    a2p === 'campaign_pending' ||
    a2p === 'approved' ||
    otpActive ||
    otpConfirmed;
  const campaignActive = a2p === 'campaign_pending';
  const campaignDone = a2p === 'approved';

  const brandState = failed && !brandDone ? 'failed' : brandDone ? 'done' : brandActive ? 'active' : 'upcoming';
  const campaignState = failed && brandDone ? 'failed' : campaignDone ? 'done' : campaignActive ? 'active' : 'upcoming';

  const steps = [
    stepRecord('profile', 'Profile', 'done'),
    stepRecord('number', 'Number', hasPhone ? 'done' : 'active'),
    stepRecord('brand', 'Brand review', brandState, {
      happening: 'Carriers are checking your business.',
      you: 'Nothing — this page updates itself. Usually hours to a couple of days.',
    }),
  ];

  if (isSoleProp) {
    const otpDone = otpConfirmed || a2p === 'campaign_pending' || a2p === 'approved';
    const otpState = failed && otpActive ? 'failed' : otpDone ? 'done' : otpActive ? 'active' : 'upcoming';
    steps.push(
      stepRecord('otp', 'OTP', otpState, {
        happening: 'Twilio texted a code to the mobile you entered.',
        you: 'Reply to that text with the code.',
        instruction: SOLE_PROP_MOBILE_OTP_COPY,
      })
    );
  }

  steps.push(
    stepRecord('campaign', 'Campaign review', campaignState, {
      happening: "Carriers are reviewing the texts you'll send.",
      you: 'Wait. Same hours-to-days window.',
    }),
    stepRecord('live', 'Live', 'upcoming')
  );

  const active = steps.find((s) => s.state === 'active' || s.state === 'failed') || null;
  return { steps, activeId: active?.id || null };
}

export function smsNextSteps({ forms = [], smsNumber = null, autoRechargeEnabled = false } = {}) {
  const formWithoutOptIn = forms.find((f) => !f.settings?.smsOptIn?.enabled);
  const formWithOptIn = forms.find((f) => f.settings?.smsOptIn?.enabled);
  const optInFormId = (formWithoutOptIn || formWithOptIn || forms[0])?.id;
  const covers = smsNumber?.a2p?.campaignCoversMissedCall === true;
  const managed = smsNumber?.externallyManaged === true;

  const rows = [
    {
      id: 'optin',
      label: 'Turn on SMS opt-in on a form',
      sub: 'Leads can check a box and get texts. Open Operator on a form.',
      done: forms.some((f) => f.settings?.smsOptIn?.enabled),
      href: optInFormId ? `/forms/${optInFormId}/edit?tab=ai-reply` : '/form-builder',
    },
    {
      id: 'forward',
      label: 'Forward calls to your phone',
      sub: managed ? EXTERNALLY_MANAGED_FORWARD_SUB : 'Your gocta number rings you.',
      done: !managed && smsNumber?.voiceEnabled === true && Boolean(smsNumber?.voiceForwardToE164),
      ...(managed ? {} : { href: '/text-messaging#call-forwarding' }),
    },
    {
      id: 'textback',
      label: 'Turn on missed-call text-back',
      sub: managed
        ? EXTERNALLY_MANAGED_TEXTBACK_SUB
        : covers
          ? 'Callers who miss you get one text.'
          : 'Needs a campaign update — request it',
      done: managed
        ? false
        : covers
          ? smsNumber?.missedCallTextBackEnabled === true
          : Boolean(smsNumber?.missedCallUpgradeRequestedAt),
    },
    ...(!managed
      ? [
          {
            id: 'test',
            label: 'Send yourself a test text',
            sub: 'Confirm the number delivers.',
            done: Boolean(smsNumber?.testSmsSentAt),
            action: 'text-me',
          },
        ]
      : []),
    {
      id: 'autocharge',
      label: 'Turn on wallet auto-recharge',
      sub: AUTO_RECHARGE_NEXT_STEPS_SUB,
      done: autoRechargeEnabled === true,
      href: AUTO_RECHARGE_BILLING_HREF,
    },
  ];

  return rows;
}
